import { Component, ElementRef, HostListener, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AdminSchoolYearService } from '../../../services/admin/admin-school-year.service';
import { AdminSchoolHealthService } from '../../../services/admin/admin-school-health.service';
import { SchoolYearModel, SchoolYearFilter } from '../../../models/school/school-year.model';
import { SchoolModel } from '../../../models/school/school.model';
import { SchoolHealthModel, SchoolHealthFilter, CheckpointField } from '../../../models/school/school-health.model';
import { ModalService } from '../../../shared/modals/modal/modal.service';
import { ErrorModel } from '../../../models/shared/error.model';
import { CountryModel } from '../../../models/localization-and-pricing/country.model';

interface OrgCheckpointStat {
  field: string;
  label: string;
  done: number;
  warning: number;
  declined: number;
  donePct: number;
  warningPct: number;
  declinedPct: number;
}

interface OrgGroup {
  key: string;
  name: string;
  schools: SchoolHealthModel[];
  numRisk: number;
  numLow: number;
  numHealthy: number;
  numStudents: number;
  numLicenses: number;
  numLicensesUsed: number;
  activeTotal: number;
  active30: number;
  active7: number;
  answersTotal: number;
  answers30: number;
  answers7: number;
  checkpoints: OrgCheckpointStat[];
}

@Component({
  selector: 'app-admin-school-year-detail',
  templateUrl: './admin-school-year-detail.component.html',
  styleUrls: ['./admin-school-year-detail.component.css']
})
export class AdminSchoolYearDetailComponent implements OnInit {

  schoolYear: SchoolYearModel = null;
  associatedSchools: SchoolModel[] = [];
  schoolSearchQuery = '';
  isLoading = false;
  isLoadingSchools = false;
  sortByName: 1 | -1 = 1;

  isEditing = false;
  isRemoving = false;
  selectedForRemoval = new Set<string>();

  activeView: 'schools' | 'health' = 'health';
  schoolHealthList: SchoolHealthModel[] = [];
  isLoadingHealth = false;
  refreshingSchoolId: string = null;
  healthSearchQuery = '';
  healthSortBy: 'risk' | 'leastActive' | 'licenseExpiry' | 'name' = 'name';
  healthFilter: 'all' | 'risk-high' | 'risk-med' | 'risk-ok' | 'no-setup' | 'expiring' = 'all';

  healthFilterDefs = [
    { key: 'all', label: 'All' },
    { key: 'risk-high', label: 'At risk' },
    { key: 'risk-med', label: 'Low activity' },
    { key: 'risk-ok', label: 'Healthy' },
    { key: 'no-setup', label: 'Not set up' },
    { key: 'expiring', label: 'Expiring soon' }
  ];

  healthGrouping: 'schools' | 'orgs' = 'schools';
  expandedOrgKeys = new Set<string>();
  orgSortBy: 'schools' | 'risk' | 'leastActive' | 'name' = 'schools';

  yearOptions: SchoolYearModel[] = [];
  isYearDropdownOpen = false;

  // Concept B — "Onboarding" dropdown chip. Shown alongside Concept A (below) for comparison.
  showCheckpointDropdown = true;
  isCheckpointDropdownOpen = false;
  checkpointFilters: { [field: string]: 'done' | 'declined' | 'warning' | 'scheduled' | 'needs-confirmation' | 'pending' } = {};

  // Concept A — second filter row dedicated to onboarding checkpoints.
  cpRowStep = '';
  cpRowState: 'done' | 'declined' | 'warning' | 'scheduled' | 'needs-confirmation' | 'pending' = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private elementRef: ElementRef,
    private schoolYearSv: AdminSchoolYearService,
    private schoolHealthSv: AdminSchoolHealthService,
    private modalSv: ModalService
  ) {}

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const dropdownEl = this.elementRef.nativeElement.querySelector('.syd-cp-dropdown-wrap');
    if (this.isCheckpointDropdownOpen && dropdownEl && !dropdownEl.contains(event.target)) {
      this.isCheckpointDropdownOpen = false;
    }
  }

  ngOnInit() {
    const id = this.route.snapshot.params['schoolYearId'];
    this.loadYear(id);
  }

  setActiveView(view: 'schools' | 'health') {
    this.activeView = view;
    if (view === 'health' && this.schoolHealthList.length === 0) {
      this.loadSchoolHealth();
    }
  }

  loadSchoolHealth() {
    this.isLoadingHealth = true;
    const filters = new SchoolHealthFilter();
    filters.schoolYearId = this.schoolYear._id;
    this.schoolHealthSv.getSchoolHealthList(filters).subscribe(list => {
      this.schoolHealthList = list;
      this.isLoadingHealth = false;
    }, () => {
      this.isLoadingHealth = false;
      this.modalSv.showAlertModal('Error', 'Could not load school health data. Please refresh the page.');
    });
  }

  refreshHealth(item: SchoolHealthModel) {
    this.refreshingSchoolId = item.schoolId;
    this.schoolHealthSv.refreshSchoolHealth(item.schoolId, item._id).subscribe(updated => {
      const index = this.schoolHealthList.findIndex(h => h._id === item._id);
      if (index !== -1) {
        this.schoolHealthList[index] = updated;
      }
      this.refreshingSchoolId = null;
    }, (err: ErrorModel) => {
      this.refreshingSchoolId = null;
      this.modalSv.showTranslatedErrorModal(err);
    });
  }

  loadYear(id: string) {
    this.isLoading = true;
    this.schoolYearSv.getSchoolYear(id).subscribe(year => {
      this.schoolYear = year;
      this.isLoading = false;
      this.loadAssociatedSchools(id);
      this.loadSchoolHealth();
      this.loadYearOptions();
    }, () => {
      this.isLoading = false;
      this.modalSv.showAlertModal('Error', 'Could not load the school year. Please go back and try again.');
    });
  }

  loadYearOptions() {
    const filters = new SchoolYearFilter();
    filters.country = this.schoolYear.country;
    this.schoolYearSv.getSchoolYears(filters).subscribe(years => {
      this.yearOptions = years;
    });
  }

  toggleYearDropdown() {
    this.isYearDropdownOpen = !this.isYearDropdownOpen;
  }

  selectYear(year: SchoolYearModel) {
    this.isYearDropdownOpen = false;
    if (year._id === this.schoolYear._id) {
      return;
    }
    this.router.navigate(['admin', 'schoolYears', year._id]);
    this.loadYear(year._id);
  }

  yearDotColorClass(year: SchoolYearModel): string {
    if (year.isClosed) {
      return 'year-dot-closed';
    }
    return year.isActive ? 'year-dot-active' : 'year-dot-pending';
  }

  get totalStudents(): number {
    return this.schoolHealthList.reduce((sum, h) => sum + (h.numStudents || 0), 0);
  }

  get dateRangeLabel(): string {
    if (!this.schoolYear) {
      return '';
    }
    return `${this.formatDate(this.schoolYear.startDate)} – ${this.formatDate(this.schoolYear.endDate)}`;
  }

  loadAssociatedSchools(id: string) {
    this.isLoadingSchools = true;
    this.schoolYearSv.getSchoolYearSchools(id, this.sortByName).subscribe(schools => {
      this.associatedSchools = schools;
      this.isLoadingSchools = false;
    }, () => {
      this.isLoadingSchools = false;
      this.modalSv.showAlertModal('Error', 'Could not load associated schools. Please refresh the page.');
    });
  }

  toggleSortByName() {
    this.sortByName = this.sortByName === 1 ? -1 : 1;
    this.loadAssociatedSchools(this.schoolYear._id);
  }

  formatDate(d: string): string {
    if (!d) {
      return '—';
    }
    return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  }

  countryTitle(code: string): string {
    return CountryModel.title(code) || code;
  }

  countryIconUrl(code: string): string {
    return CountryModel.iconURL(code);
  }

  get currentCountryTitle(): string {
    if (!this.schoolYear) {
      return '';
    }
    return this.countryTitle(this.schoolYear.country);
  }

  get filteredSchools(): SchoolModel[] {
    if (!this.schoolSearchQuery) {
      return this.associatedSchools;
    }
    const q = this.schoolSearchQuery.toLowerCase();
    return this.associatedSchools.filter(s => s.name.toLowerCase().includes(q));
  }

  activationRate(item: SchoolHealthModel): number {
    return item.numLicensesUsed > 0 ? Math.round((item.numLicensesActivated / item.numLicensesUsed) * 100) : 0;
  }

  private metricColor(value: number, lowMax: number, midMax: number): string {
    if (value < lowMax) {
      return '#e03d3d';
    }
    if (value < midMax) {
      return '#f7941d';
    }
    return '#2d9140';
  }

  activationRateColor(item: SchoolHealthModel): string {
    return this.metricColor(this.activationRate(item), 50, 75);
  }

  answers30Color(item: SchoolHealthModel): string {
    return this.metricColor(item.numAnswersLast30Days || 0, 100, 500);
  }

  active30Color(item: SchoolHealthModel): string {
    return this.metricColor(item.numActiveStudentsLast30Days || 0, 10, 30);
  }

  activeStudentsPercentOfLicensesUsed(item: SchoolHealthModel): number {
    if (item.numLicensesUsed <= 0) { return 0; }
    return Math.min(100, Math.round(((item.numActiveStudentsLast30Days || 0) / item.numLicensesUsed) * 100));
  }

  activeStudentsPctColor(item: SchoolHealthModel): string {
    return this.metricColor(this.activeStudentsPercentOfLicensesUsed(item), 30, 60);
  }

  avgAnswersPerActiveStudent(item: SchoolHealthModel): number {
    return item.numActiveStudentsLast30Days > 0 ? Math.round((item.numAnswersLast30Days || 0) / item.numActiveStudentsLast30Days) : 0;
  }

  isLicenseExpiringSoon(item: SchoolHealthModel): boolean {
    if (!item.licenseExpiresAt) {
      return false;
    }
    const days = (new Date(item.licenseExpiresAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24);
    return days >= 0 && days <= 30;
  }

  schoolRisk(item: SchoolHealthModel): 'risk-high' | 'risk-med' | 'risk-ok' {
    const colors = [this.activationRateColor(item), this.answers30Color(item), this.active30Color(item)];
    if (colors.includes('#e03d3d')) {
      return 'risk-high';
    }
    if (colors.includes('#f7941d')) {
      return 'risk-med';
    }
    return 'risk-ok';
  }

  riskColor(item: SchoolHealthModel): string {
    const risk = this.schoolRisk(item);
    return risk === 'risk-high' ? '#e03d3d' : risk === 'risk-med' ? '#f7941d' : '#3ab54a';
  }

  healthBadges(item: SchoolHealthModel): { text: string, bg: string, color: string, border: string }[] {
    const badges: { text: string, bg: string, color: string, border: string }[] = [];
    const green = { bg: 'rgba(58,181,74,.10)', color: '#2d9140', border: 'rgba(58,181,74,.22)' };
    const amber = { bg: 'rgba(247,148,29,.10)', color: '#9a5010', border: 'rgba(247,148,29,.25)' };
    const red = { bg: 'rgba(224,61,61,.08)', color: '#b02020', border: 'rgba(224,61,61,.22)' };

    if (this.isLicenseExpiringSoon(item)) {
      badges.push({ text: 'License expiring', ...red });
    }
    if (badges.length === 0 && this.schoolRisk(item) === 'risk-ok') {
      badges.push({ text: 'Healthy', ...green });
    }
    return badges;
  }

  lastActiveLabel(item: SchoolHealthModel): string {
    return item.lastAnswerDate ? this.formatDate(item.lastAnswerDate) : 'Never';
  }

  setHealthFilter(key: 'all' | 'risk-high' | 'risk-med' | 'risk-ok' | 'no-setup' | 'expiring') {
    this.healthFilter = key;
  }

  toggleCheckpointDropdown() {
    this.isCheckpointDropdownOpen = !this.isCheckpointDropdownOpen;
  }

  toggleCheckpointFilterChip(field: string, state: 'done' | 'declined' | 'warning' | 'scheduled' | 'needs-confirmation' | 'pending') {
    if (this.checkpointFilters[field] === state) {
      delete this.checkpointFilters[field];
    } else {
      this.checkpointFilters[field] = state;
    }
  }

  clearCheckpointFilters() {
    this.checkpointFilters = {};
  }

  get checkpointFilterCount(): number {
    return Object.keys(this.checkpointFilters).length;
  }

  toggleCpRowState(state: 'done' | 'declined' | 'warning' | 'scheduled' | 'needs-confirmation' | 'pending') {
    this.cpRowState = this.cpRowState === state ? null : state;
  }

  clearCpRow() {
    this.cpRowStep = '';
    this.cpRowState = null;
  }

  cpStateLabel(state: 'done' | 'declined' | 'warning' | 'scheduled' | 'needs-confirmation' | 'pending'): string {
    const labels = { done: 'Done', pending: 'Not done', warning: 'Needs review', declined: 'Declined', scheduled: 'Scheduled', 'needs-confirmation': 'Awaiting confirmation' };
    return labels[state] || '';
  }

  cpStepLabel(field: string): string {
    if (!field) {
      return 'Any step';
    }
    const cp = this.CHECKPOINTS.find(c => c.field === field);
    return cp ? cp.label : 'Any step';
  }

  get checkpointStats(): { field: string, label: string, step: number, done: number, warning: number, declined: number, scheduled: number, needsConfirmation: number, pending: number, donePct: number, warningPct: number, declinedPct: number, scheduledPct: number, needsConfirmationPct: number, numClass: string, icon: string }[] {
    const total = this.schoolHealthList.length;
    return this.CHECKPOINTS.map(cp => {
      let done = 0;
      let warning = 0;
      let declined = 0;
      let pending = 0;
      let scheduled = 0;
      let needsConfirmation = 0;
      this.schoolHealthList.forEach(item => {
        const status = this.cpStatus(item, cp.field);
        if (status === 'done') { done++; }
        else if (status === 'warning') { warning++; }
        else if (status === 'declined') { declined++; }
        else if (status === 'scheduled') { scheduled++; }
        else if (status === 'needs-confirmation') { needsConfirmation++; }
        else { pending++; }
      });
      const pct = (n: number) => total > 0 ? Math.round((n / total) * 100) : 0;
      let numClass = '';
      let icon = String(cp.step);
      if (warning > 0) {
        numClass = 'has-warn';
        icon = '!';
      } else if (needsConfirmation > 0) {
        numClass = 'needs-confirmation';
        icon = '?';
      } else if (total > 0 && done === total) {
        numClass = 'all-done';
        icon = '✓';
      }
      return {
        field: cp.field, label: cp.label, step: cp.step,
        done, warning, declined, scheduled, needsConfirmation, pending,
        donePct: pct(done), warningPct: pct(warning), declinedPct: pct(declined),
        scheduledPct: pct(scheduled), needsConfirmationPct: pct(needsConfirmation),
        numClass, icon
      };
    });
  }

  get healthSummary(): { risk: number, low: number, healthy: number, active: number } {
    return this.schoolHealthList.reduce((acc, item) => {
      const risk = this.schoolRisk(item);
      if (risk === 'risk-high') {
        acc.risk++;
      } else if (risk === 'risk-med') {
        acc.low++;
      } else {
        acc.healthy++;
      }
      acc.active += item.numActiveStudentsLast30Days || 0;
      return acc;
    }, { risk: 0, low: 0, healthy: 0, active: 0 });
  }

  get filteredHealthList(): SchoolHealthModel[] {
    let list = this.schoolHealthList;
    if (this.healthSearchQuery && this.healthGrouping === 'schools') {
      const q = this.healthSearchQuery.toLowerCase();
      list = list.filter(h => (h.schoolName || '').toLowerCase().includes(q));
    }
    if (this.healthFilter !== 'all') {
      list = list.filter(h => {
        if (this.healthFilter === 'no-setup') {
          return !h.hasAnyGroupSetUp || !h.hasAnyObjectiveAssigned;
        }
        if (this.healthFilter === 'expiring') {
          return this.isLicenseExpiringSoon(h);
        }
        return this.schoolRisk(h) === this.healthFilter;
      });
    }
    const checkpointFields = Object.keys(this.checkpointFilters);
    if (checkpointFields.length > 0) {
      list = list.filter(h => checkpointFields.every(field => this.cpStatus(h, field) === this.checkpointFilters[field]));
    }
    if (this.cpRowState) {
      if (this.cpRowStep) {
        list = list.filter(h => this.cpStatus(h, this.cpRowStep) === this.cpRowState);
      } else {
        list = list.filter(h => this.CHECKPOINTS.some(cp => this.cpStatus(h, cp.field) === this.cpRowState));
      }
    }
    const riskOrder = (h: SchoolHealthModel) => {
      const r = this.schoolRisk(h);
      return r === 'risk-high' ? 0 : r === 'risk-med' ? 1 : 2;
    };
    return [...list].sort((a, b) => {
      if (this.healthSortBy === 'risk') {
        return riskOrder(a) - riskOrder(b);
      }
      if (this.healthSortBy === 'leastActive') {
        return (a.numActiveStudentsLast30Days || 0) - (b.numActiveStudentsLast30Days || 0);
      }
      if (this.healthSortBy === 'licenseExpiry') {
        return new Date(a.licenseExpiresAt || 0).getTime() - new Date(b.licenseExpiresAt || 0).getTime();
      }
      return (a.schoolName || '').localeCompare(b.schoolName || '');
    });
  }

  get searchPlaceholder(): string {
    if (this.healthGrouping === 'orgs') {
      return 'Search organizations…';
    }
    return 'Search schools…';
  }

  setHealthGrouping(grouping: 'schools' | 'orgs') {
    this.healthGrouping = grouping;
  }

  toggleOrg(key: string) {
    if (this.expandedOrgKeys.has(key)) {
      this.expandedOrgKeys.delete(key);
    } else {
      this.expandedOrgKeys.add(key);
    }
  }

  isOrgExpanded(key: string): boolean {
    return this.expandedOrgKeys.has(key);
  }

  pctOf(value: number, total: number): number {
    if (!total) {
      return 0;
    }
    return Math.min(100, Math.round(((value || 0) / total) * 100));
  }

  formatCompact(n: number): string {
    const value = n || 0;
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 10000) {
      return `${Math.round(value / 1000)}k`;
    }
    return String(value);
  }

  // Groups the currently filtered schools by organization. Every school of an organization
  // is kept in `schools` so the expanded panel lists all of them, not a subset.
  get orgGroups(): OrgGroup[] {
    const groups = new Map<string, OrgGroup>();
    this.filteredHealthList.forEach(item => {
      const key = item.organizationId || item.organizationName || 'none';
      let group = groups.get(key);
      if (!group) {
        group = {
          key,
          name: item.organizationName || 'No organization',
          schools: [],
          numRisk: 0, numLow: 0, numHealthy: 0,
          numStudents: 0, numLicenses: 0, numLicensesUsed: 0,
          activeTotal: 0, active30: 0, active7: 0,
          answersTotal: 0, answers30: 0, answers7: 0,
          checkpoints: this.CHECKPOINTS.map(cp => ({ field: cp.field, label: cp.label, done: 0, warning: 0, declined: 0, donePct: 0, warningPct: 0, declinedPct: 0 }))
        };
        groups.set(key, group);
      }
      group.schools.push(item);
      const risk = this.schoolRisk(item);
      if (risk === 'risk-high') {
        group.numRisk++;
      } else if (risk === 'risk-med') {
        group.numLow++;
      } else {
        group.numHealthy++;
      }
      group.numStudents += item.numStudents || 0;
      group.numLicenses += item.numLicenses || 0;
      group.numLicensesUsed += item.numLicensesUsed || 0;
      group.activeTotal += item.numActiveStudentsThisYear || 0;
      group.active30 += item.numActiveStudentsLast30Days || 0;
      group.active7 += item.numActiveStudentsLast7Days || 0;
      group.answersTotal += item.numAnswersThisYear || 0;
      group.answers30 += item.numAnswersLast30Days || 0;
      group.answers7 += item.numAnswersLast7Days || 0;
      group.checkpoints.forEach(cp => {
        const status = this.cpStatus(item, cp.field);
        if (status === 'done') {
          cp.done++;
        } else if (status === 'warning') {
          cp.warning++;
        } else if (status === 'declined') {
          cp.declined++;
        }
      });
    });
    const list = Array.from(groups.values());
    list.forEach(group => {
      group.checkpoints.forEach(cp => {
        cp.donePct = this.pctOf(cp.done, group.schools.length);
        cp.warningPct = this.pctOf(cp.warning, group.schools.length);
        cp.declinedPct = this.pctOf(cp.declined, group.schools.length);
      });
    });
    list.forEach(group => {
      group.schools.sort((a, b) => this.compareSchoolsForOrgSort(a, b));
    });
    let result = list;
    if (this.healthSearchQuery) {
      const q = this.healthSearchQuery.toLowerCase();
      result = result.filter(g => g.name.toLowerCase().includes(q));
    }
    return result.sort((a, b) => {
      if (this.orgSortBy === 'schools' && b.schools.length !== a.schools.length) {
        return b.schools.length - a.schools.length;
      }
      if (this.orgSortBy === 'risk' && b.numRisk !== a.numRisk) {
        return b.numRisk - a.numRisk;
      }
      if (this.orgSortBy === 'leastActive' && a.active30 !== b.active30) {
        return a.active30 - b.active30;
      }
      return a.name.localeCompare(b.name);
    });
  }

  // Schools inside an expanded organization follow the same sort as the organization list.
  private compareSchoolsForOrgSort(a: SchoolHealthModel, b: SchoolHealthModel): number {
    if (this.orgSortBy === 'risk') {
      const order = { 'risk-high': 0, 'risk-med': 1, 'risk-ok': 2 };
      const diff = order[this.schoolRisk(a)] - order[this.schoolRisk(b)];
      if (diff !== 0) {
        return diff;
      }
    }
    if (this.orgSortBy === 'leastActive') {
      const diff = (a.numActiveStudentsLast30Days || 0) - (b.numActiveStudentsLast30Days || 0);
      if (diff !== 0) {
        return diff;
      }
    }
    return (a.schoolName || '').localeCompare(b.schoolName || '');
  }

  orgInitials(name: string): string {
    if (!name) {
      return '';
    }
    return name.substring(0, 3).toUpperCase();
  }

  readonly CHECKPOINTS: { field: string; label: string; step: number }[] = [
    { field: 'licenseSetup',          label: 'License',    step: 1 },
    { field: 'groupsAndStudentSetup', label: 'Groups',     step: 2 },
    { field: 'teacherTraining',       label: 'Training',   step: 3 },
    { field: 'bookOrder',             label: 'Books',      step: 4 },
    { field: 'studentOnboarding',     label: 'Onboarding', step: 5 },
  ];

  cardAccentClass(item: SchoolHealthModel): string {
    if (!item.numLicensesUsed && !item.numStudents) { return 'accent-indigo'; }
    if (this.schoolRisk(item) === 'risk-ok') { return 'accent-green'; }
    return 'accent-orange';
  }

  avatarColorClass(item: SchoolHealthModel): string {
    if (!item.numLicensesUsed && !item.numStudents) { return 'indigo'; }
    if (this.schoolRisk(item) === 'risk-ok') { return 'green'; }
    return 'orange';
  }

  readonly SCHEDULABLE_FIELDS: string[] = ['teacherTraining', 'studentOnboarding'];

  cpSupportsScheduling(field: string): boolean {
    return this.SCHEDULABLE_FIELDS.indexOf(field) !== -1;
  }

  cpIsFutureDate(dateStr: string): boolean {
    if (!dateStr) { return false; }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const d = new Date(dateStr);
    d.setHours(0, 0, 0, 0);
    return d.getTime() > today.getTime();
  }

  cpStatus(item: SchoolHealthModel, field: string): 'done' | 'declined' | 'warning' | 'scheduled' | 'needs-confirmation' | 'pending' {
    const cp = (item as any)[field] as CheckpointField;
    if (cp && cp.declined) { return 'declined'; }
    if (field === 'groupsAndStudentSetup' && !(cp && cp.date) && (item.numGroupsWithUnresolvedReview || 0) > 0) { return 'warning'; }
    if (cp && cp.date && this.cpSupportsScheduling(field)) {
      if (cp.confirmed !== false) { return 'done'; }
      return this.cpIsFutureDate(cp.date) ? 'scheduled' : 'needs-confirmation';
    }
    if (cp && cp.date) { return 'done'; }
    return 'pending';
  }

  cpDotContent(step: number, status: 'done' | 'declined' | 'warning' | 'scheduled' | 'needs-confirmation' | 'pending'): string {
    if (status === 'done')     { return '✓'; }
    if (status === 'declined') { return '✕'; }
    if (status === 'warning')  { return '!'; }
    if (status === 'needs-confirmation') { return '?'; }
    return String(step);
  }

  cpDateLabel(item: SchoolHealthModel, field: string, status: 'done' | 'declined' | 'warning' | 'scheduled' | 'needs-confirmation' | 'pending'): string {
    const cp = (item as any)[field] as CheckpointField;
    if (status === 'pending')  { return '—'; }
    if (status === 'warning')  { return 'Review'; }
    if (status === 'scheduled') { return `Scheduled ${cp && cp.date ? this.formatShortDate(cp.date) : ''}`.trim(); }
    if (status === 'needs-confirmation') { return 'Confirm?'; }
    if (cp && cp.date) { return this.formatShortDate(cp.date); }
    if (status === 'declined') { return 'Declined'; }
    return '—';
  }

  avgPerActiveStudent(answers: number, activeStudents: number): string {
    if (!activeStudents) { return '—'; }
    return String(Math.round((answers || 0) / activeStudents));
  }

  formatShortDate(d: string): string {
    if (!d) { return '—'; }
    return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  }

  goBack() {
    this.router.navigate(['admin', 'schoolYears']);
  }

  openAssociate() {
    this.router.navigate(['admin', 'schoolYears', this.schoolYear._id, 'associate']);
  }

  openResearchExport() {
    this.router.navigate(['admin', 'schoolYears', this.schoolYear._id, 'researchExport']);
  }

  toggleEditing() {
    this.isEditing = !this.isEditing;
    this.selectedForRemoval.clear();
  }

  isSelectedForRemoval(schoolId: string): boolean {
    return this.selectedForRemoval.has(schoolId);
  }

  toggleSelectedForRemoval(schoolId: string) {
    if (this.selectedForRemoval.has(schoolId)) {
      this.selectedForRemoval.delete(schoolId);
    } else {
      this.selectedForRemoval.add(schoolId);
    }
  }

  get allSelectedForRemoval(): boolean {
    return this.filteredSchools.length > 0 && this.filteredSchools.every(s => this.selectedForRemoval.has(s._id));
  }

  get someSelectedForRemoval(): boolean {
    return this.filteredSchools.some(s => this.selectedForRemoval.has(s._id));
  }

  toggleSelectAllForRemoval(checked: boolean) {
    if (checked) {
      this.filteredSchools.forEach(s => this.selectedForRemoval.add(s._id));
    } else {
      this.filteredSchools.forEach(s => this.selectedForRemoval.delete(s._id));
    }
  }

  confirmRemoveSelected() {
    const count = this.selectedForRemoval.size;
    if (count === 0) {
      return;
    }
    const message = `This will remove ${count} school${count !== 1 ? 's' : ''} from this school year. Continue?`;
    this.modalSv.showChoiceModal('Remove schools', message).subscribe(confirmed => {
      if (confirmed) {
        this.removeSelected();
      }
    });
  }

  removeSelected() {
    const schoolIds = Array.from(this.selectedForRemoval);
    this.isRemoving = true;
    this.schoolYearSv.bulkRemoveSchoolFromSchoolYear(schoolIds, this.schoolYear._id).subscribe(result => {
      this.isRemoving = false;
      this.selectedForRemoval.clear();
      this.isEditing = false;
      this.loadAssociatedSchools(this.schoolYear._id);
      if (result.failed > 0) {
        this.modalSv.showAlertModal('Some removals failed', `${result.removed} school${result.removed !== 1 ? 's' : ''} removed, ${result.failed} failed. Please try again.`);
      }
    }, () => {
      this.isRemoving = false;
      this.modalSv.showAlertModal('Error', 'Could not remove the selected schools. Please try again.');
    });
  }

  schoolInitials(name: string): string {
    if (!name) {
      return '';
    }
    return name.substring(0, 2).toUpperCase();
  }
}
